import { el, clear, panel, empty, chip, button, table, toast } from '../ui.js';
import { get, ApiError } from '../api.js';
import { LOSS_MEANING, type LossReason } from '../../../../packages/core/src/outcomes.js';

/**
 * Why this agency stops winning people.
 *
 * ---------------------------------------------------------------------------
 * THE SCREEN NO COMPETING PRODUCT HAS
 * ---------------------------------------------------------------------------
 *
 * Every CRM in this market reports contact outcomes — no answer, voicemail,
 * callback, sold, not interested. Checked against a real insurance CRM in
 * September 2026: "Not Interested" was the only decline disposition it offered,
 * with no granularity underneath it at all.
 *
 * So an agency knows exactly how many people said no and nothing whatever about
 * what they said no to. A book that is unaffordable, a book that is unhealthy, a
 * book already covered, and a book that does not remember asking for the call
 * are four different businesses with four different fixes — and they look
 * identical in every competing product.
 *
 * ---------------------------------------------------------------------------
 * THE PART THIS SCREEN REFUSES TO DO
 * ---------------------------------------------------------------------------
 *
 * It will not put a vendor comparison in front of somebody without saying which
 * half of it can carry an argument.
 *
 * A comparison over every loss is confounded: unless leads were assigned at
 * random, it measures the AGENTS who happened to work each vendor's list. The
 * best closer gets the best leads, that list then reports the best numbers, and
 * the report confirms the decision that produced it. Every dialer in this market
 * ships that number with no caveat on it.
 *
 * So both are shown, labelled, with the randomised one marked as the one to
 * quote at a vendor — and the balance check on top, because a randomised slice
 * that silently stopped being filled leaves every figure here looking exactly as
 * it does now while meaning nothing.
 */

interface LossBreakdown {
  total: number;
  byReason: Array<{ reason: LossReason; count: number; share: number }>;
  otherRate: number;
  unrecordedRate: number;
}
interface SourceQuality {
  sourceId: string;
  losses: number;
  badLead: number;
  badLeadRate: number | null;
  noRecollection: number;
  noRecollectionRate: number | null;
  thin: boolean;
}
interface AgentAgreement {
  agentId: string;
  comparable: number;
  agreed: number;
  agreementRate: number | null;
  topDivergence: { saidByAgent: LossReason; heardOnCall: LossReason; count: number } | null;
  thin: boolean;
}
interface Report {
  days: number;
  breakdown: LossBreakdown;
  bySource: SourceQuality[];
  bySourceRandom: SourceQuality[];
  byAgent: AgentAgreement[];
  holdout: {
    total: number; holdout: number; share: number; drift: number; suspect: boolean;
    target: number;
    /** Leads that went through an import, and so could have been randomised. */
    imported: number;
    /** Every lead. Context, never the denominator — see `neverImported`. */
    book: number;
    /** Nothing imported yet, so nothing could have been randomised. A fact, not an alarm. */
    neverImported: boolean;
  };
  noDerivedReasons: boolean;
}

const pct = (n: number | null): string => (n === null ? '—' : `${Math.round(n * 100)}%`);
const label = (r: LossReason): string => LOSS_MEANING[r]?.label ?? r;

export function lossesView(go: (p: string) => void): HTMLElement {
  const host = el('div');
  const stage = el('div');

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Reports' }),
    el('h1', { text: 'Why we lose' }),
    el('p', {
      text: 'Every other system records that somebody said no. This records what they said no '
        + 'to — and separates the part of it you can take to a lead vendor from the part you cannot.',
    })));
  host.append(stage);

  const load = async (days: number): Promise<void> => {
    clear(stage);
    stage.append(el('div', { class: 'note', text: 'Reading…' }));
    try {
      const r = await get<Report>(`/v1/reports/losses?days=${days}`);
      clear(stage);
      draw(stage, r, load, go);
    } catch (e) {
      clear(stage);
      const msg = e instanceof ApiError ? e.message : 'Could not read the report.';
      stage.append(el('div', { class: 'warnbox' }, el('b', null, msg)));
      toast(msg, 'crit');
    }
  };
  void load(90);
  return host;
}

function draw(host: HTMLElement, r: Report, reload: (d: number) => Promise<void>, go: (p: string) => void): void {
  const windows = el('div', { style: 'display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap' });
  for (const d of [30, 90, 365]) {
    const b = button(d === 365 ? 'Past year' : `Past ${d} days`, d === r.days ? 'sm pri' : 'sm',
      () => void reload(d));
    windows.append(b);
  }
  host.append(windows);

  /* --------------------------------------------------- the breakdown --- */
  if (r.breakdown.total === 0) {
    host.append(panel('Nothing lost yet', empty(
      'No losses recorded in this window',
      'A reason is written down every time a call ends without a sale. Once the floor has '
      + 'been dialling, this is where the pattern appears — and it is the pattern nobody '
      + 'else in this market can see at all.',
    )));
  } else {
    const rows = el('div');
    for (const b of r.breakdown.byReason) {
      const meaning = LOSS_MEANING[b.reason];
      const bar = el('div', {
        style: `height:8px;border-radius:4px;background:var(--iris);width:${Math.max(2, b.share * 100)}%`,
      });
      rows.append(el('div', { style: 'margin-bottom:12px' },
        el('div', { style: 'display:flex;gap:10px;align-items:baseline;flex-wrap:wrap' },
          el('b', { text: label(b.reason) }),
          el('span', { class: 'qhour', text: `${b.count.toLocaleString()} · ${Math.round(b.share * 100)}%` }),
          meaning?.recoverable ? chip('worth trying again', 'ok') : null,
          meaning?.vendorSignal ? chip('about the lead, not the person', 'crit') : null),
        el('div', { style: 'margin-top:5px;background:var(--line);border-radius:4px' }, bar)));
    }
    host.append(panel(`${r.breakdown.total.toLocaleString()} losses`, rows,
      /*
       * The taxonomy's own error bars, on the same screen as the taxonomy.
       * A rising `other` means these categories have stopped describing what
       * happens on the telephone, and the honest response is to change them
       * rather than keep filing. A rising blank rate is a screen problem or a
       * time problem, and it says which before anybody argues about discipline.
       */
      el('div', { class: r.breakdown.otherRate > 0.2 || r.breakdown.unrecordedRate > 0.3 ? 'warnbox' : 'note' },
        el('b', null, `${pct(r.breakdown.otherRate)} came back "other" · `),
        `${pct(r.breakdown.unrecordedRate)} had no reason recorded at all. `,
        r.breakdown.otherRate > 0.2
          ? 'A fifth in "other" means these categories have stopped describing what is actually '
            + 'happening on the telephone. That is a reason to change them, not to keep filing.'
          : r.breakdown.unrecordedRate > 0.3
            ? 'A third with nothing recorded is a screen problem or a time problem, not a '
              + 'discipline problem. The reason is meant to be one tap during the call.'
            : 'Both low enough to trust the rest of this page.')));
  }

  /* ------------------------------------------------------- the vendors --- */
  host.append(panel('Where the leads came from',
    /*
     * THE DISTINCTION THIS WHOLE SCREEN EXISTS TO MAKE.
     */
    el('div', { class: 'note' },
      el('b', null, 'Two tables, and only one of them settles an argument. '),
      'Above the line is every loss — which is what any other system would show you, and it is '
      + 'confounded: unless leads were handed out at random, it measures the agents who happened '
      + 'to work each list. Below the line is the randomised slice only. Nothing varies with the '
      + 'vendor there except the vendor.'),
    sourceTable('Every loss — indicative only', r.bySource, false),
    sourceTable('Randomised slice — this is the one to quote at a vendor', r.bySourceRandom, true),
    holdoutNote(r.holdout, go)));

  /* -------------------------------------------------------- the agents --- */
  host.append(panel('What the agent said, and what the call said',
    r.noDerivedReasons
      ? el('div', { class: 'note' },
        el('b', null, 'Nothing derives a reason from the call yet. '),
        'This compares the reason an agent recorded against the reason the transcript supports. '
        + 'Until transcription is reading calls there is nothing to compare, and a blank table '
        + 'here would otherwise look exactly like perfect agreement.')
      : agentTable(r.byAgent)));
}

function sourceTable(title: string, rows: SourceQuality[], authoritative: boolean): HTMLElement {
  const box = el('div', { style: 'margin-top:14px' },
    el('div', { style: 'display:flex;gap:8px;align-items:baseline;margin-bottom:8px' },
      el('b', { text: title }),
      authoritative ? chip('comparable', 'ok') : chip('confounded', 'crit')));
  if (!rows.length) {
    box.append(el('div', { class: 'qhour', text: 'Nothing in this window.' }));
    return box;
  }
  box.append(table(
    ['Source', 'Losses', 'About the lead', '"I never asked"'],
    rows.map((s) => ({
      cells: [
        el('code', { text: s.sourceId }),
        s.losses.toLocaleString(),
        s.thin
          ? el('span', { class: 'qhour', text: 'too few' })
          : el('span', { text: `${pct(s.badLeadRate)} (${s.badLead})` }),
        s.thin
          ? el('span', { class: 'qhour', text: '—' })
          // The single most valuable number on the page: somebody who does not
          // remember asking for a quote is a lead the vendor should not have sold.
          : el('b', { text: `${pct(s.noRecollectionRate)} (${s.noRecollection})` }),
      ],
    })),
  ));
  if (rows.some((s) => s.thin)) {
    box.append(el('div', { class: 'qhour', style: 'margin-top:6px' },
      'A source with fewer than thirty losses shows no rate at all. A vendor conversation '
      + 'that starts "four of your leads didn\'t remember filling anything in" is a '
      + 'conversation about four people.'));
  }
  return box;
}

function holdoutNote(h: Report['holdout'], go: (p: string) => void): HTMLElement {
  if (h.book === 0) {
    return el('div', { class: 'note', style: 'margin-top:14px' },
      'No leads in the book yet, so nothing has been randomised.');
  }
  if (h.neverImported) {
    /*
     * A FACT, NOT AN ALARM — and the difference matters.
     *
     * The coin flip happens at import. An agency that has only typed leads in
     * by hand has an empty holdout for a reason that is not a fault, and
     * shouting at them about it every day is how a check that catches a real
     * and otherwise invisible failure gets ignored.
     */
    return el('div', { class: 'note', style: 'margin-top:14px' },
      el('b', null, 'Nothing has been imported yet, so nothing has been randomised. '),
      `All ${h.book.toLocaleString()} of these leads were entered by hand, and a hand-entered `
      + 'lead is a referral rather than part of a vendor comparison — so it never gets the coin '
      + 'flip. The randomised table stays empty until a list is imported.',
      el('div', { style: 'margin-top:10px' },
        button('Import a list', 'sm', () => go('/import'))));
  }
  if (h.suspect) {
    /*
     * THE FAILURE THIS EXISTS FOR. A holdout that quietly stopped being
     * assigned leaves every number above looking exactly as it does now and
     * meaning nothing. Silence would be the worst possible outcome.
     */
    return el('div', { class: 'warnbox', style: 'margin-top:14px' },
      el('b', null, 'The randomised slice is not the size it should be. '),
      `${h.holdout.toLocaleString()} of ${h.imported.toLocaleString()} imported leads `
      + `(${pct(h.share)}) against a target of ${pct(h.target)}. `,
      'Until that is explained, treat the randomised table above as unreliable — a slice that '
      + 'stopped being filled produces numbers that look completely normal.');
  }
  return el('div', { class: 'note', style: 'margin-top:14px' },
    `${h.holdout.toLocaleString()} of ${h.imported.toLocaleString()} imported leads (${pct(h.share)}) `
    + `are assigned at random, against a target of ${pct(h.target)}. `,
    el('b', null, 'That is what makes the second table worth anything. '),
    'It cannot be added later — history gathered without it is confounded permanently.');
}

function agentTable(rows: AgentAgreement[]): HTMLElement {
  if (!rows.length) {
    return el('div', { class: 'qhour', text: 'Nothing to compare in this window.' });
  }
  const built = table(
    ['Agent', 'Comparable calls', 'Agreed', 'Most common difference'],
    rows.map((a) => ({
      cells: [
        el('code', { text: a.agentId }),
        a.comparable.toLocaleString(),
        a.thin
          ? el('span', { class: 'qhour', text: 'too few' })
          : el('span', { text: pct(a.agreementRate) }),
        a.topDivergence
          ? el('span', { class: 'qhour' },
            `recorded "${label(a.topDivergence.saidByAgent)}", call suggested `
            + `"${label(a.topDivergence.heardOnCall)}" — ${a.topDivergence.count}×`)
          : el('span', { class: 'qhour', text: '—' }),
      ],
    })),
  );
  return el('div', null, built,
    // Shaped as a conversation, not a ranking. An agent who learns this table
    // scores them starts recording the reason that scores well.
    el('div', { class: 'note', style: 'margin-top:10px' },
      el('b', null, 'This is not a scoreboard. '),
      'It is where an agent\'s reading of a call and the call itself most often differ, which is '
      + 'the most useful thing anybody can take into a coaching conversation. The agent was on the '
      + 'call; the transcript was not. Where they diverge, the agent is usually right and '
      + 'occasionally learning something.'));
}
